import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, Checkbox, InputNumber } from 'antd';
import { isEmpty } from 'lodash';

import { cleanEmptyString } from 'utils/helpers';
import diff from 'diffObj';
import {
  FLEX_SHIP_MAXIMUM_AMOUNT,
  FLEX_SHIP_MINIMUM_AMOUNT
} from 'constants/flex-ship';
import { maxNumberRule, minNumberRule, requiredValueRule } from 'utils/forms';
import { getCurrency } from 'common/countries/countries-mapping';
import { enableBusinessFlexShip } from 'services/business';

import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';

import './FlexShipSettings.less';

const FlexShipSettings = ({ businessData, ...props }) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const initialValuesRef = useRef({
    amountToBeCollected: businessData?.flexShip?.amountToBeCollected,
    isAppliedToAllOrders: businessData?.flexShip?.isAppliedToAllOrders
  });
  const [isEditing, setIsEditing] = useState({
    amountToBeCollected: false,
    isAppliedToAllOrders: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values) => {
    const initial = initialValuesRef?.current;

    const newValueKey = Object.keys(values)[0];
    handleEditState(newValueKey);

    const isNewPayload = diff(
      cleanEmptyString(initial),
      cleanEmptyString(values)
    );

    if (isEmpty(isNewPayload)) {
      return;
    }

    try {
      setIsLoading(true);

      const { isAllowedToSubscribe, ...restFlexShip } =
        businessData?.flexShip || {};

      const payload = { ...restFlexShip, ...values };

      await enableBusinessFlexShip(businessData?._id, payload);

      notify({
        msg: intl.formatMessage({
          id: 'flexShip.subscribe_success'
        }),
        type: 'success'
      });
    } catch (error) {
      notify({ msg: error.message, error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnChangeForm = (name, value) => {
    form.setFieldsValue({ [name]: parseInt(value) });
  };

  const handleEditState = (field) => {
    setIsEditing((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleEditOrSaveAmount = () => {
    if (isEditing.amountToBeCollected) {
      form.submit();
    } else {
      handleEditState('amountToBeCollected');
    }
  };

  return (
    <div className="br-settings-flexShip">
      <LoadingWrapper loading={isLoading}>
        <Form
          form={form}
          onFinish={handleSubmit}
          name="flexShipForm"
          initialValues={initialValuesRef.current}
        >
          <div className="br-settings-flexShip__amount-input">
            <Form.Item
              name="amountToBeCollected"
              label={
                <>
                  <span className="caption_md">
                    {intl.formatMessage({
                      id: 'flexShip.input_title'
                    })}
                  </span>
                </>
              }
              rules={[
                requiredValueRule({
                  message: intl.formatMessage({
                    id: 'flexShip.required_amount'
                  })
                }),

                minNumberRule({
                  message: intl.formatMessage({
                    id: 'flexShip.minimum_amount'
                  }),
                  min: FLEX_SHIP_MINIMUM_AMOUNT
                }),
                maxNumberRule({
                  message: intl.formatMessage({
                    id: 'flexShip.maximum_amount'
                  }),
                  max: FLEX_SHIP_MAXIMUM_AMOUNT
                })
              ]}
            >
              <InputNumber
                disabled={!isEditing.amountToBeCollected}
                isWithCounter={false}
                prefix={getCurrency().localized}
                filedName="amountToBeCollected"
                handleOnChangeForm={handleOnChangeForm}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <BRButton
              className="button-md"
              type="basic"
              label={intl.formatMessage({
                id: `common.${isEditing.amountToBeCollected ? 'save' : 'edit'}`
              })}
              onClick={handleEditOrSaveAmount}
            />
          </div>

          <div className="br-settings-flexShip__allow-switch">
            <Form.Item name="isAppliedToAllOrders" valuePropName="checked">
              <Checkbox
                size="small"
                onChange={(e) =>
                  handleSubmit({ isAppliedToAllOrders: e.target.checked })
                }
              />
            </Form.Item>
            <span className="caption_md">
              {intl.formatMessage({
                id: 'flexShip.allow_all_orders'
              })}
            </span>
          </div>
        </Form>
      </LoadingWrapper>
    </div>
  );
};

export default FlexShipSettings;
